<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <VTableRemote
    v-else
    :iconHeader="hasPermission('documents.types.add') ? 'add' : null"
    :items="displayedTypes"
    isSearchable
    hideDefaultFooter
    :isInfinitScroll="true"
    :headers="tableHeaders"
    :itemActions="actions"
    :isCustomIcon="true"
    :title="$t('documents.types.pageName')"
    :pagination="pagination"
    :centerColumns="['action']"
    :notSortableColumns="['action']"
    :sortableColumns="['name']"
    @iconAction="onClickAction"
    @sendQueryParams="fetchQuery"
  >
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </VTableRemote>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import NameForm from '@/components/forms/NameForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {
        VTableRemote,
    },
    data: () => ({
        types: [],
        pagination: {},
        queryParams: {
            page: 1,
            perpage,
            search: '',
            pagination: true,
        },
        loading: false,
    }),
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [{
                text: this.$t('forms.labels.name'),
                value: 'name',
                sortable: true,
            }];
        },
        displayedTypes() {
            return this.types.map(type => ({
                id: type.id,
                name: type.name[this.userLang] && type.name[this.userLang].length
                    ? type.name[this.userLang] : type.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('documents.types.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('documents.types.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getTypes();
        this.loading = false;
    },
    methods: {
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, search: queryParams?.search || '', page: 1 };
            this.pagination = {};
            this.types = [];
            await this.getTypes();
        },
        async getTypes() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getDocumentTypes(cleanQueryParams);
            this.types = [...this.types, ...data.types];
            this.pagination = data.pagination;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('documents.types.add'),
                component: Vue.component('NameForm', NameForm),
                isForm: true,
                showLangs: true,
                onConfirm: requestPayload => this.addType(requestPayload),
            });
        },
        async onClickAction(type, action) {
            if (!action && type === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getDocumentType(type.id);
                this.$root.$emit('showModal', {
                    title: this.$t('documents.types.update'),
                    component: Vue.component('NameForm', NameForm),
                    isForm: true,
                    showLangs: true,
                    payload: { name: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateType(type.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('documents.types.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('documents.types.confirmRemove') },
                    onConfirm: () => this.removeType(type),
                    isForm: false,
                });
            }
        },
        async addType(requestPayload) {
            await this.$rq.addDocumentType(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('documents.types.added', { name: requestPayload.name[this.lang] }),
            });
            this.fetchQuery({});
        },
        async updateType(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateDocumentType(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('documents.types.updated', { name: requestPayload.name[this.lang] }),
            });
            this.fetchQuery({});
        },
        async removeType(type) {
            await this.$rq.removeDocumentType(type.id);
            this.$toasted.global.toast_success({
                message: this.$t('documents.types.removed', { name: type.name[this.lang] }),
            });
            this.fetchQuery({});
        },

        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getTypes();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
